import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TuiButtonModule,
  TuiLoaderModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
  TuiHostedDropdownModule,
  TuiDropdownModule,
  TuiDialogService,
} from '@taiga-ui/core';
import { TuiInputModule, TuiInputDateRangeModule } from '@taiga-ui/kit';
import { DecimalPipe } from '@angular/common';
import { InventoryService } from '../../core/services/inventory.service';
import { AuthService } from '../../modules/auth/services/auth.service';
import { TuiAlertService } from '@taiga-ui/core';
import { TUI_PROMPT, TuiPromptData } from '@taiga-ui/kit';

// Inventory item interface
interface InventoryItem {
  id: string;
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  dropdownOpen: boolean;
}

@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    FormsModule,
    TuiButtonModule,
    TuiLoaderModule,
    TuiSvgModule,
    TuiTextfieldControllerModule,
    TuiInputModule,
    TuiInputDateRangeModule,
    DecimalPipe,
    TuiHostedDropdownModule,
    TuiDropdownModule,
  ],
  providers: [InventoryService, AuthService],
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
})
export class InventoryComponent implements OnInit {
  searchText = '';
  tempSearchText = '';
  showSearch = false;
  statusMsg = false;
  isLoading = false;
  inventoryList: InventoryItem[] = [];
  filteredList: InventoryItem[] = [];

  constructor(
    private router: Router,
    private inventoryService: InventoryService,
    private authService: AuthService,
    private alertService: TuiAlertService,
    private readonly dialogs: TuiDialogService
  ) {}

  ngOnInit(): void {
    this.loadInventory();
  }

  async loadInventory(): Promise<void> {
    this.isLoading = true;
    try {
      const data = await this.inventoryService.getAllInventory(this.searchText);

      const itemsArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];

      this.inventoryList = itemsArray.map((item: any) => ({
        id: item._id,
        itemId: item.itemId,
        name: item.itemName || '',
        quantity: item.quantity || 0,
        price: item.price || 0,
        dropdownOpen: false,
      }));

      this.applyFilter();
      this.checkLowStock();
    } catch (err) {
      console.error('Error loading inventory:', err);
      this.inventoryList = [];
      this.filteredList = [];
    } finally {
      this.isLoading = false;
    }
  }

  // Toggle advanced search
  toggle(): void {
    this.showSearch = !this.showSearch;
  }

  // Cancel search event
  cancelSearch(event: any): void {
    if (!event) {
      this.resetStatus();
    }
  }

  // Full search reloads inventory from API
  async search(): Promise<void> {
    this.statusMsg = true;
    this.tempSearchText = this.searchText;
    await this.loadInventory();
  }

  // Reset search and status
  resetStatus(): void {
    this.searchText = '';
    this.tempSearchText = '';
    this.statusMsg = false;
    this.applyFilter();
  }

  // Navigate to edit page using itemId
  updateItem(item: InventoryItem): void {
    if (!item.id) return;
    this.router.navigate(['admin/inventory/add-edit', item.id]);
  }

  // Delete item using itemId
  async deleteItem(item: InventoryItem): Promise<void> {
    if (!item.id) return;
    const data: TuiPromptData = {
      content: `Are you sure you want to delete Inventory Item: <b>${item.name}</b>?`,
      yes: 'Delete',
      no: 'Cancel',
    };
    this.dialogs
      .open<boolean>(TUI_PROMPT, { size: 's', data })
      .subscribe((response) => {
        if (response) {
          this.inventoryService
            .deleteInventoryItem(item.id)
            .then(() => this.loadInventory())
            .catch((err) => console.error('Failed to delete item:', err));
        }
      });
  }

  // Navigate to add new inventory
  navigateToAdd(): void {
    this.router.navigate(['/admin/inventory/add-edit']);
  }

  // Apply filter based on search text
  private applyFilter(): void {
    const text = this.searchText.toLowerCase();
    this.filteredList = this.inventoryList.filter(
      (item) =>
        item.name.toLowerCase().includes(text) || String(item.id).includes(text)
    );
  }

  // Show status text for filtered results
  showText(): string {
    return this.statusMsg && this.tempSearchText
      ? `Showing Results for "${this.tempSearchText}"`
      : '';
  }

  private checkLowStock(): void {
    const lowStockItems = this.inventoryList.filter(
      (item) => item.quantity <= 20
    );

    if (lowStockItems.length > 0) {
      lowStockItems.forEach((item) => {
        this.alertService
          .open(
            `${item.name} is low on stock (only ${item.quantity} left). Please restock!`,
            {
              status: 'warning',
              autoClose: false,
            }
          )
          .subscribe();
      });
    }
  }
}
